import React, { useEffect } from 'react'
import { Form, Modal, Input, Select, useForm } from '@pankod/refine-antd'
import { useDelete, useGetIdentity } from '@pankod/refine-core'

import { TLMSCommonError } from 'src/interfaces/common'
import {
  getAssignableAdminRoleOptions,
  getEditableAdminFields,
} from '../role-acl'
import { buildAdminFormPayload } from '../form-utils'

import type {
  TLMSAdmin,
  TLMSAdminFormValues,
  TLMSAdminModalState,
  TLMSRoleOption,
  TLMSAdminRole,
} from '../types'

type TModalLMSAdminProps = {
  modalState: TLMSAdminModalState
  setModalState: (value: TLMSAdminModalState) => void
  roleOptions: TLMSRoleOption[]
}

export const ModalLMSAdmin: React.FC<TModalLMSAdminProps> = (props) => {
  const { modalState, setModalState, roleOptions } = props
  const [confirmationForm, setConfirmationForm] = React.useState<string>('')
  const modalType = modalState?.type
  const selectedAdmin =
    modalState && modalState.type !== 'create' ? modalState.admin : null

  const { mutateAsync, isLoading } = useDelete()

  const { form, formProps, saveButtonProps } = useForm<
    TLMSAdmin,
    TLMSCommonError,
    TLMSAdminFormValues
  >({
    action: 'create',
    resource: 'admins',
    dataProviderName: 'lms',
    redirect: false,
    onMutationSuccess: () => {
      form.resetFields()
      setModalState(null)
    },
    successNotification: {
      message: `Success ${(modalType || 'create').toUpperCase()} User's Role!`,
      type: 'success',
    },
    errorNotification(error) {
      return {
        description: 'Error',
        type: 'error',
        message:
          (error as TLMSCommonError).response?.data.error.details?.[0]
            ?.message ||
          `There was a problem when ${modalType || 'create'} admin user`,
      }
    },
  })

  useEffect(() => {
    if (!modalState) return

    if (modalState.type === 'create') {
      form.resetFields()
      return
    }

    form.setFieldsValue(modalState.admin)
  }, [form, modalState])

  const { data } = useGetIdentity<{ user: { LMSRole: TLMSAdminRole } }>()
  const LMSRole = data?.user?.LMSRole || ''

  const editableFields =
    modalType === 'update' && selectedAdmin
      ? getEditableAdminFields({
          actorRole: LMSRole,
          targetRole: selectedAdmin.role,
        })
      : []
  const canEditName = editableFields.includes('name')
  const canEditRole = editableFields.includes('role')
  const availableRoleOptions = getAssignableAdminRoleOptions({
    actorRole: LMSRole,
    mode: modalType === 'create' ? 'create' : 'update',
    roleOptions,
  })

  const isModalTypeNeedConfirmation =
    modalType === 'update' || modalType === 'delete'

  const handleDelete = async () => {
    await mutateAsync(
      {
        id: '',
        resource: 'admins',
        dataProviderName: 'lms',
        values: {
          email: form.getFieldValue('email'),
          role: form.getFieldValue('role'),
        },
      },
      {
        onSuccess: () => {
          form.resetFields()
          setModalState(null)
        },
      },
    )
  }

  const handleFormFinish = (values: TLMSAdminFormValues) => {
    formProps.onFinish?.(
      buildAdminFormPayload({
        mode: modalType === 'update' ? 'update' : 'create',
        values,
        editableFields,
      }),
    )
  }

  const getModalTitle = () => {
    switch (modalType) {
      case 'create':
        return 'Tambahkan Admin Baru'
      case 'update':
        return 'Ubah Informasi Akun Admin'
      case 'delete':
        return 'Hapus Akun Admin'
      default:
        return ''
    }
  }

  return (
    <Modal
      visible={!!modalState}
      title={getModalTitle()}
      okText="Simpan"
      cancelText="Batal"
      onCancel={() => {
        form.resetFields()
        setConfirmationForm('')
        setModalState(null)
      }}
      onOk={() => {
        if (isModalTypeNeedConfirmation) {
          if (modalType === 'delete') {
            handleDelete()
            return
          }

          if (confirmationForm === form.getFieldValue('email')) {
            saveButtonProps.onClick()
          }
          return
        }
        saveButtonProps.onClick()
      }}
      okButtonProps={{
        danger: isModalTypeNeedConfirmation,
        disabled:
          isModalTypeNeedConfirmation &&
          confirmationForm !== form.getFieldValue('email'),
        loading: isLoading,
      }}
    >
      <Form<TLMSAdminFormValues>
        {...formProps}
        onFinish={handleFormFinish}
        layout="vertical"
        validateMessages={{ required: '${label} must be filled.' }}
      >
        <Form.Item
          label="User Email"
          name="email"
          rules={[
            {
              required: true,
              type: 'email',
              message: '${label} is not a valid email.',
            },
            // email is belajar.id
            // {
            //   pattern: /@belajar\.id$/,
            //   message: '${label} must be a belajar.id email.',
            // },
            // email is not registered
            // {
            //   validator: async (_, value) => {
            //     // TODO: complete this
            //   },
            // },
          ]}
        >
          <Input
            type="email"
            autoComplete="off"
            disabled={modalType === 'update' || modalType === 'delete'}
          />
        </Form.Item>
        {modalType === 'create' || modalType === 'update' ? (
          <Form.Item
            label="Nama"
            name="name"
            rules={
              modalType === 'update' && canEditName
                ? [
                    {
                      required: true,
                      whitespace: true,
                    },
                  ]
                : undefined
            }
          >
            <Input
              autoComplete="off"
              disabled={modalType === 'update' && !canEditName}
            />
          </Form.Item>
        ) : null}
        <Form.Item
          label="Role"
          name="role"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select
            disabled={
              modalType === 'delete' || (modalType === 'update' && !canEditRole)
            }
            options={availableRoleOptions}
          />
        </Form.Item>
      </Form>

      {isModalTypeNeedConfirmation && (
        <>
          <p>Isi alamat email yang hendak diubah sebagai konfirmasi</p>
          <Input
            className="mt-2"
            type="email"
            autoComplete="off"
            placeholder="Alamat email dari akun yang akan diubah"
            value={confirmationForm}
            onChange={(e) => setConfirmationForm(e.target.value)}
          />
        </>
      )}
    </Modal>
  )
}
